/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { IOutsourced } from "../../../../interfaces/outsourcedes";
import { ECustomerSupplierLinkResult, ICustomerSupplierConflict } from "../../../../interfaces/customersSuppliers";
import {
  copyCustomerSupplierConflictData,
  getCustomerSupplierConflict,
  hasCustomerSupplierChanges,
} from "@/utils/customerSupplierSync";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import useMounted from "../../../../hooks/mounted";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  listOutsourcedes,
  showOutsourced,
  createOutsourced,
  updateOutsourced,
  reactivateOutsourced,
  deleteOutsourced,
} from "@/services";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const DEFAULT_MAINTENANCE: IOutsourced = {
  name: "",
  corporateReason: "",
  document: "",
  address: "",
  cep: "",
  city: "",
  state: "",
  location: { type: 'Point', coordinates: [0, 0] },
  coveredStates: [],
  coveredScopes: [],
  coveredServices: [],
  phone: "",
  email: "",
  statusEhs: true,
};

export const useOutsourcedes = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { idOutsourced: id } = useParams();
  const location = useLocation();
  const [outsourcedes, setOutsourcedes] = useState<IOutsourced[]>([]);
  const [outsourced, setOutsourced] = useState<IOutsourced>(DEFAULT_MAINTENANCE);
  const [savedOutsourced, setSavedOutsourced] = useState<IOutsourced>(DEFAULT_MAINTENANCE);
  const [confirmSyncOpen, setConfirmSyncOpen] = useState<boolean>(false);
  const [customerSupplierResult, setCustomerSupplierResult] = useState<ECustomerSupplierLinkResult>();
  const [customerSupplierConflict, setCustomerSupplierConflict] = useState<ICustomerSupplierConflict>();
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: defaultLimit || 10 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [enableChangePositionMap, setEnableChangePositionMap] = useState<boolean>(false);
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/outsourcedes" });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOutsourced = (
    key: string,
    value: any,
    custom?: (v: any) => any,
  ) => {
    setOutsourced((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = ({
    page = 1,
    limit,
    sort,
  }: {
    page?: number;
    limit?: number;
    sort?: any;
  }) => {
    setLoading(true);
    listOutsourcedes({ includes: [...includes], filter, page, limit, sort })
      .then(
        async ({
          outsourcedes: listOutsourcedes,
          limit,
          currentPage,
          totalItems,
        }) => {
          setOutsourcedes(listOutsourcedes);

          setLimit(Number(limit));
          setTotalItems(Number(totalItems));
        },
      )
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listOutsourcedes, key: "outsourcedes", includes, filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showOutsourced({ id })
      .then(({ outsourced: outsourcedesShow }) => {
        setOutsourced(outsourcedesShow);
        setSavedOutsourced(outsourcedesShow);
      })
      .finally(() => setLoading(false));
  };

  /** Abre o modal de conflito quando o documento já pertence a outro cadastro com dados diferentes. */
  const handleSaveError = (error: unknown) => {
    const conflict = getCustomerSupplierConflict(error);
    if (conflict) setCustomerSupplierConflict(conflict);
  };

  const createWith = (data: IOutsourced) => {
    setLoading(true);
    createOutsourced({ outsourced: data })
      .then(({ outsourced: outsourcedCreate, customerSupplierResult: result }) => {
        toast.success(`Terceirizada criado com sucesso!`);
        setOutsourced(outsourcedCreate);
        setSavedOutsourced(outsourcedCreate);
        setCustomerSupplierConflict(undefined);
        setCustomerSupplierResult(result);
        goEdit(outsourcedCreate.id!);
      })
      .catch(handleSaveError)
      .finally(() => setLoading(false));
  };

  const updateWith = (data: IOutsourced) => {
    setLoading(true);
    updateOutsourced({ id: data.id!, outsourced: data })
      .then(({ outsourced: outsourcedUpdate, customerSupplierResult: result }) => {
        toast.success(`Terceirizada atualizado com sucesso!`);
        setSavedOutsourced(outsourcedUpdate);
        setCustomerSupplierConflict(undefined);
        if (result !== ECustomerSupplierLinkResult.AlreadyExisted) setCustomerSupplierResult(result);
        goEdit(outsourcedUpdate.id!);
      })
      .catch(handleSaveError)
      .finally(() => setLoading(false));
  };

  const create = () => createWith(outsourced);

  const update = () => updateWith(outsourced);

  /** Copia para o formulário os dados do cadastro que já usa o documento e salva com eles. */
  const copyConflictAndSave = () => {
    if (!customerSupplierConflict) return;
    const data = copyCustomerSupplierConflictData(outsourced, customerSupplierConflict);
    setOutsourced(data);
    if (data.id) updateWith(data);
    else createWith(data);
  };

  /** Pede confirmação antes de salvar quando a edição altera também o cliente/fornecedor do Financeiro. */
  const requestUpdate = () => {
    if (hasCustomerSupplierChanges(savedOutsourced, outsourced)) {
      setConfirmSyncOpen(true);
      return;
    }
    update();
  };

  const confirmSync = () => {
    setConfirmSyncOpen(false);
    update();
  };

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteOutsourced({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Terceirizada",
        plural: "terceirizadas",
        gender: "f",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, sort });
    }
  };

  const activate = () => {
    setLoading(true);
    reactivateOutsourced({ id: outsourced.id! })
      .then(({ outsourced: outsourcedActivated }) => {
        toast.success(`Terceirizada reativada com sucesso!`);
        setOutsourced(outsourcedActivated);
      })
      .finally(() => setLoading(false));
  };

  useMounted(() => {
    if (!disabledGetAll) getAll({ page, limit, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    const isPage = location.pathname.includes("/towermanagement/outsourcedes");
    if (id && isPage && !disabledShowId) {
      if (id !== "new") get({ id: id });
    }
  }, []);
  return {
    outsourcedes,
    outsourced,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    deleteMany,
    goBack,
    goAdd,
    handleOutsourced,
    create,
    id,
    update,
    requestUpdate,
    confirmSyncOpen,
    confirmSync,
    cancelSync: () => setConfirmSyncOpen(false),
    customerSupplierResult,
    closeCustomerSupplierResult: () => setCustomerSupplierResult(undefined),
    customerSupplierConflict,
    closeCustomerSupplierConflict: () => setCustomerSupplierConflict(undefined),
    copyConflictAndSave,
    activate,
    enableChangePositionMap,
    setEnableChangePositionMap,
    totalItems,
    search,
    handleFilter,
    handleSort,
  };
};
